/**
 * MouseTouch.ts — Drag (pan) + scroll/pinch zoom + clicks.
 *
 * Fase 6:
 *   shift+arrastar     -> retangulo de selecao (div HTML #swRetangulo por cima do canvas)
 *   botao direito      -> envio rapido (o menu de contexto do navegador fica bloqueado no canvas)
 *   ctrl/cmd+click     -> envio rapido (trackpad sem botao direito)
 *   duplo clique       -> todos os meus planetas
 *   toque              -> os mesmos clicks (o navegador gera mousedown/up depois do toque)
 * Distingue click (sem movimento) vs drag (>4px). Click vai pra Selecao.
 */

import type { Camera } from '../render/Camera';
import type { Selecao } from './Selecao';
import type { LinhaSelecao, } from '../render/LinhaSelecao';
import { planetaEm } from '../render/LinhaSelecao';
import type { EstadoJogo } from '../domain/tipos';

interface DragState {
  x: number;
  y: number;
  camX: number;
  camY: number;
  moveu: boolean;
}

export class MouseTouch {
  private dragging = false;
  private dragStart: DragState = { x: 0, y: 0, camX: 0, camY: 0, moveu: false };
  private lastPinchDist: number | null = null;
  private retangulo: { x0: number; y0: number } | null = null;
  private readonly elRetangulo: HTMLElement | null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: Camera,
    private readonly selecao: Selecao,
    private readonly preview: LinhaSelecao,
    private readonly estado: EstadoJogo,
  ) {
    this.elRetangulo = document.getElementById('swRetangulo');
  }

  iniciar(): void {
    // Mouse
    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    window.addEventListener('mousemove', (e) => this.onMouseMove(e));
    window.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('dblclick', (e) => this.onDblClick(e));

    // Scroll zoom
    this.canvas.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });

    // Touch
    this.canvas.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
    this.canvas.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.canvas.addEventListener('touchend', (e) => this.onTouchEnd(e));
  }

  private onMouseDown(e: MouseEvent): void {
    if (e.button === 2) return;                 // botao direito: so o mouseup conta
    if (e.button !== 0) return;
    if (e.shiftKey) {
      this.retangulo = { x0: e.clientX, y0: e.clientY };
      this.desenharRetangulo(e.clientX, e.clientY);
      return;
    }
    this.dragging = true;
    this.dragStart = {
      x: e.clientX,
      y: e.clientY,
      camX: this.camera.alvoX,
      camY: this.camera.alvoY,
      moveu: false,
    };
    this.canvas.style.cursor = 'grabbing';
  }

  private onMouseMove(e: MouseEvent): void {
    if (this.retangulo) {
      this.desenharRetangulo(e.clientX, e.clientY);
      return;
    }
    if (this.selecao.quantidade > 0 && !this.dragging) {
      this.preview.desenhar(this.selecao.ids, e.clientX, e.clientY);
    }
    if (!this.dragging) return;

    const dx = e.clientX - this.dragStart.x;
    const dy = e.clientY - this.dragStart.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      this.dragStart.moveu = true;
      this.camera.setAlvo(
        this.dragStart.camX - dx / this.camera.zoom,
        this.dragStart.camY - dy / this.camera.zoom,
      );
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (this.retangulo) {
      const { x0, y0 } = this.retangulo;
      this.retangulo = null;
      this.esconderRetangulo();
      if (Math.abs(e.clientX - x0) < 4 && Math.abs(e.clientY - y0) < 4) {
        this.selecao.processarClick(e.clientX, e.clientY, { somar: true });
      } else {
        const a = this.camera.telaParaMundo(x0, y0);
        const b = this.camera.telaParaMundo(e.clientX, e.clientY);
        this.selecao.selecionarRetangulo(a.x, a.y, b.x, b.y);
      }
      return;
    }
    if (e.button === 2) {
      this.selecao.processarClick(e.clientX, e.clientY, { rapido: true });
      return;
    }
    if (!this.dragging) return;
    const foiClick = !this.dragStart.moveu;
    this.dragging = false;
    this.canvas.style.cursor = 'default';
    if (foiClick) {
      this.selecao.processarClick(e.clientX, e.clientY, {
        somar: e.shiftKey,
        rapido: e.ctrlKey || e.metaKey,
      });
    }
  }

  private onDblClick(e: MouseEvent): void {
    const mundo = this.camera.telaParaMundo(e.clientX, e.clientY);
    const p = planetaEm(this.estado, mundo.x, mundo.y);
    if (p && p.donoId === this.estado.suaCor) this.selecao.selecionarTodos();
  }

  private desenharRetangulo(x: number, y: number): void {
    const el = this.elRetangulo;
    if (!el || !this.retangulo) return;
    const { x0, y0 } = this.retangulo;
    el.style.display = 'block';
    el.style.left = `${Math.min(x0, x)}px`;
    el.style.top = `${Math.min(y0, y)}px`;
    el.style.width = `${Math.abs(x - x0)}px`;
    el.style.height = `${Math.abs(y - y0)}px`;
  }

  private esconderRetangulo(): void {
    if (this.elRetangulo) this.elRetangulo.style.display = 'none';
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const fator = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.camera.setAlvo(this.camera.alvoX, this.camera.alvoY, this.camera.alvoZoom * fator);
  }

  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      this.dragging = true;
      this.dragStart = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        camX: this.camera.alvoX,
        camY: this.camera.alvoY,
        moveu: false,
      };
    } else if (e.touches.length === 2) {
      this.dragging = false;
      this.lastPinchDist = distEntreTouches(e.touches);
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 1 && this.dragging) {
      const dx = (e.touches[0].clientX - this.dragStart.x) / this.camera.zoom;
      const dy = (e.touches[0].clientY - this.dragStart.y) / this.camera.zoom;
      this.camera.setAlvo(this.dragStart.camX - dx, this.dragStart.camY - dy);
      e.preventDefault();
    } else if (e.touches.length === 2) {
      const dist = distEntreTouches(e.touches);
      if (this.lastPinchDist !== null) {
        const fator = dist / this.lastPinchDist;
        this.camera.setAlvo(
          this.camera.alvoX,
          this.camera.alvoY,
          this.camera.alvoZoom * fator,
        );
      }
      this.lastPinchDist = dist;
      e.preventDefault();
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) this.dragging = false;
    if (e.touches.length < 2) this.lastPinchDist = null;
  }
}

function distEntreTouches(touches: TouchList): number {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}
